import React, { useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Login from './components/Login';
import Register from './components/Register';
import Lobby from './components/Lobby';
import Room from './components/Room';
import Settings from './components/Settings';
import Calls from './components/Calls';
import Users from './components/Users';
import PrivateChat from './components/PrivateChat';
import Rooms from './components/Rooms';
import './App.css';
import { socket } from './socket';
import sodium from 'libsodium-wrappers';

const isTokenValid = (token) => {
  try {
    const decoded = jwtDecode(token); // Updated to use named export
    if (decoded && typeof decoded === 'object' && decoded.exp) {
      return Date.now() < decoded.exp * 1000;
    }
    return true;
  } catch {
    return false;
  }
};

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token || !isTokenValid(token)) {
    localStorage.removeItem('token');
    return <Navigate to="/login" />;
  }
  return children;
};

const AppContent = () => {
  const decryptMessage = useCallback(async (encryptedMessage, senderPublicKeyB64, currentUser) => {
    if (!currentUser) return '[DECRYPTION ERROR: Not logged in]';
    const storedKey = localStorage.getItem(`privateKey_${currentUser.username}`);
    if (!storedKey) return '[DECRYPTION ERROR: Private key not found]';

    try {
      await sodium.ready;
      const privateKey = sodium.from_base64(storedKey, sodium.base64_variants.URLSAFE);
      const ciphertext = sodium.from_base64(encryptedMessage.ciphertext, sodium.base64_variants.URLSAFE);
      const nonce = sodium.from_base64(encryptedMessage.nonce, sodium.base64_variants.URLSAFE);
      const senderPublicKey = sodium.from_base64(senderPublicKeyB64, sodium.base64_variants.URLSAFE);
      
      const decrypted = sodium.crypto_box_open_easy(ciphertext, nonce, senderPublicKey, privateKey);
      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.error('Decryption failed in App:', error);
      return '[Decryption Failed]';
    }
  }, []);

  const navigate = useNavigate(); // Use navigate hook inside the component

  useEffect(() => {
    const handleInvitation = ({ roomId, roomName }) => {
      if (window.confirm(`You have been invited to join the room: ${roomName}. Do you want to join?`)) {
        navigate(`/room/${roomId}`);
      }
    };

    socket.on('invited-to-room', handleInvitation);

    return () => {
      socket.off('invited-to-room', handleInvitation);
    };
  }, [navigate]);

  useEffect(() => {
    const handlePrivateMessage = async ({ senderUsername, encryptedMessage }) => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const currentUser = jwtDecode(token);
        const usersData = JSON.parse(localStorage.getItem('users_data') || '[]');
        const sender = usersData.find(u => u.username === senderUsername);

        if (sender) {
          const decryptedText = await decryptMessage(encryptedMessage, sender.publicKey, currentUser);
          const message = { text: decryptedText, sender: senderUsername, recipient: currentUser.username, timestamp: Date.now() };

          const chatHistory = JSON.parse(localStorage.getItem(`chat_${senderUsername}_${currentUser.username}`) || '[]');
          chatHistory.push(message);
          localStorage.setItem(`chat_${senderUsername}_${currentUser.username}`, JSON.stringify(chatHistory));

          // Notify other components of the new message
          window.dispatchEvent(new CustomEvent('new-private-message', { detail: message }));
        }
      } catch (e) {
        console.error('Failed to process private message:', e);
      }
    };

    socket.on('receive-private-message', handlePrivateMessage);

    return () => {
      socket.off('receive-private-message', handlePrivateMessage);
    };
  }, [decryptMessage]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        socket.auth = { username: decoded.username };
        socket.connect();
        socket.emit('join', { username: decoded.username });
      } catch (e) {
        console.error('Invalid token for socket connection', e);
      }
    }

    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <div className="App">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route 
          path="/"
          element={
            <PrivateRoute>
              <Lobby />
            </PrivateRoute>
          }
        />
        <Route 
          path="/settings"
          element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          }
        />
        <Route 
          path="/calls"
          element={
            <PrivateRoute>
              <Calls />
            </PrivateRoute>
          }
        />
        <Route 
          path="/rooms"
          element={
            <PrivateRoute>
              <Rooms />
            </PrivateRoute>
          }
        />
        <Route 
          path="/users"
          element={
            <PrivateRoute>
              <Users />
            </PrivateRoute>
          }
        />
        <Route 
          path="/room/:roomId" 
          element={
            <PrivateRoute>
              <Room />
            </PrivateRoute>
          }
        />
        <Route 
          path="/chat/:username" 
          element={
            <PrivateRoute>
              <PrivateChat />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
